/**
 * @fileoverview App Settings page
 * Allows users to configure app-wide settings like language
 */

// Third-party imports
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { ChevronLeft } from "lucide-react";
import { Link } from "@tanstack/react-router";

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

// Hooks
import { useCompanyDetails, useUpdateCompanyDetails } from "@/features/company-details/hooks/use-company-details";
import { CURRENCY_FORMATS, type CurrencyFormatCode } from "@/hooks/use-currency-format";

/**
 * AppSettingsPage Component
 * Displays app-wide settings including language and date format selection
 */
function AppSettingsPage() {
  const { t, i18n } = useTranslation();
  const { data: companyDetails } = useCompanyDetails();
  const updateCompanyDetails = useUpdateCompanyDetails();
  
  const [language, setLanguage] = useState<string>(
    localStorage.getItem('language') || 'browser'
  );
  const [dateFormat, setDateFormat] = useState<string>('DD.MM.YYYY');
  const [currencyFormat, setCurrencyFormat] = useState<string>('DE');

  // Load date format from company details
  useEffect(() => {
    if (companyDetails?.dateFormat) {
      setDateFormat(companyDetails.dateFormat);
    }
    if (companyDetails?.currencyFormat) {
      setCurrencyFormat(companyDetails.currencyFormat);
    }
  }, [companyDetails]);

  const handleLanguageChange = (value: string) => {
    setLanguage(value);
    
    if (value === 'browser') {
      // Use browser language
      const browserLang = navigator.language?.split('-')[0] || 'de';
      const supportedLang = ['de', 'en'].includes(browserLang) ? browserLang : 'de';
      i18n.changeLanguage(supportedLang);
      localStorage.setItem('language', 'browser');
    } else {
      // Use selected language
      i18n.changeLanguage(value);
      localStorage.setItem('language', value);
    }
    
    toast.success(t('settings.appSettings.saved'));
  };

  const handleDateFormatChange = (value: string) => {
    setDateFormat(value);
    updateCompanyDetails.mutate(
      { dateFormat: value },
      {
        onSuccess: () => {
          toast.success(t('settings.appSettings.dateFormatSaved'));
        },
        onError: () => {
          toast.error(t('settings.appSettings.dateFormatError'));
        },
      }
    );
  };

  const handleCurrencyFormatChange = (value: string) => {
    setCurrencyFormat(value);
    updateCompanyDetails.mutate(
      { currencyFormat: value },
      {
        onSuccess: () => {
          toast.success('Währungsformat gespeichert');
        },
        onError: () => {
          toast.error('Fehler beim Speichern des Währungsformats');
        },
      }
    );
  };

  // Format examples for current date
  const exampleDate = new Date(2025, 0, 15); // January 15, 2025
  const getDateExample = (format: string) => {
    switch (format) {
      case 'DD.MM.YYYY':
        return '15.01.2025';
      case 'MM/DD/YYYY':
        return '01/15/2025';
      case 'YYYY-MM-DD':
        return '2025-01-15';
      case 'DD/MM/YYYY':
        return '15/01/2025';
      case 'DD-MM-YYYY':
        return '15-01-2025';
      default:
        return '15.01.2025';
    }
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Link
        to="/dashboard/settings"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" />
        {t('common.cancel')}
      </Link>

      {/* Language Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.appSettings.title')}</CardTitle>
          <CardDescription>{t('settings.appSettings.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Language Setting */}
          <div className="space-y-2">
            <Label htmlFor="language">{t('settings.appSettings.language')}</Label>
            <p className="text-sm text-muted-foreground">
              {t('settings.appSettings.languageDescription')}
            </p>
            <Select value={language} onValueChange={handleLanguageChange}>
              <SelectTrigger id="language" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="browser">{t('settings.appSettings.browserLanguage')}</SelectItem>
                <SelectItem value="de">{t('settings.appSettings.german')}</SelectItem>
                <SelectItem value="en">{t('settings.appSettings.english')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Date Format Setting */}
          <div className="space-y-2">
            <Label htmlFor="dateFormat">{t('settings.appSettings.dateFormat')}</Label>
            <p className="text-sm text-muted-foreground">
              {t('settings.appSettings.dateFormatDescription')}
            </p>
            <Select value={dateFormat} onValueChange={handleDateFormatChange}>
              <SelectTrigger id="dateFormat" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="DD.MM.YYYY">
                  DD.MM.YYYY <span className="text-muted-foreground ml-2">({getDateExample('DD.MM.YYYY')})</span>
                </SelectItem>
                <SelectItem value="MM/DD/YYYY">
                  MM/DD/YYYY <span className="text-muted-foreground ml-2">({getDateExample('MM/DD/YYYY')})</span>
                </SelectItem>
                <SelectItem value="YYYY-MM-DD">
                  YYYY-MM-DD <span className="text-muted-foreground ml-2">({getDateExample('YYYY-MM-DD')})</span>
                </SelectItem>
                <SelectItem value="DD/MM/YYYY">
                  DD/MM/YYYY <span className="text-muted-foreground ml-2">({getDateExample('DD/MM/YYYY')})</span>
                </SelectItem>
                <SelectItem value="DD-MM-YYYY">
                  DD-MM-YYYY <span className="text-muted-foreground ml-2">({getDateExample('DD-MM-YYYY')})</span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Currency Format Setting */}
          <div className="space-y-2">
            <Label htmlFor="currencyFormat">Währungsformat</Label>
            <p className="text-sm text-muted-foreground">
              Wählen Sie das Format für die Darstellung von Geldbeträgen in Rechnungen und der Benutzeroberfläche
            </p>
            <Select value={currencyFormat} onValueChange={handleCurrencyFormatChange}>
              <SelectTrigger id="currencyFormat" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CURRENCY_FORMATS).map(([code, format]) => (
                  <SelectItem key={code} value={code}>
                    {format.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Route configuration for the app settings page
 */
export const Route = createFileRoute("/(app)/dashboard/settings/app-settings")({
  component: AppSettingsPage,
  errorComponent: ({ error }: { error: Error }) => (
    <div role="alert" className="p-4">
      <h2 className="text-xl font-bold">Error Loading App Settings</h2>
      <pre className="mt-4">{error.message}</pre>
    </div>
  ),
});
